import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FintechAddComponent } from '../fintech-add/fintech-add.component';
import { StripeCardElement, StripeElements } from "@stripe/stripe-js";
import { FintechService } from "../../../../services/fintech.service";
import { HttpClient } from "@angular/common/http";
import { FormBuilder } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
// Import the necessary RxJS operators and services
import { Observable, of, switchMap, Subject, EMPTY } from "rxjs";
import { retry, catchError, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-add-stripe',
    templateUrl: './fintech-add-stripe.component.html',
    styleUrl: './fintech-add-stripe.component.scss'
})
export class FintechAddStripeComponent extends FintechAddComponent implements OnInit, OnDestroy {

    @ViewChild('cardElement') cardElementRef: ElementRef;
    elements: StripeElements | null = null;
    card: StripeCardElement | null = null;
    private destroy$ = new Subject<void>();

    constructor(fintechService: FintechService,
                http: HttpClient,
                formBuilder: FormBuilder,
                changeDetectorRef: ChangeDetectorRef,
                private dialogRef: MatDialogRef<FintechAddStripeComponent>,
                private toastr: ToastrService // Inject ToastrService
    ) {
        super(fintechService, http, formBuilder, changeDetectorRef);
    }

    ngOnInit(): void {
        this.cardInfoChanged = false;
        this.changeDetectorRef.detectChanges();
        this.elements = this.fintechService.stripe.elements();
        this.card = this.elements.create('card', {
            hidePostalCode: true, // Always hide the postal code field
            style: {
                base: {
                    color: '#2196F3',
                    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                    fontSmoothing: 'antialiased',
                    fontSize: '16px',
                    '::placeholder': {
                        color: '#2196F3'
                    },
                    ':-webkit-autofill': {
                        color: '#2196F3'
                    }
                },
                complete: {
                    color: '#2196F3'
                },
                invalid: {
                    color: '#fa755a',
                    iconColor: '#fa755a'
                }
            }
        });
        this.card.mount(this.cardElementRef.nativeElement);
        this.card.on('change', () => {
            this.cardInfoChanged = true;
        });

    }


    onBillingCancel() {
        this.dialogRef.close(null);
    }


    onBillingSubmit() {
        this.payMethodInfo.cardholder = this.billForm.value.cardholder;
        this.payMethodInfo.address1 = this.billForm.value.address1;
        this.payMethodInfo.address2 = this.billForm.value.address2;
        this.payMethodInfo.city = this.billForm.value.city;
        this.payMethodInfo.state = this.billForm.value.state;
        this.payMethodInfo.postalCode = this.billForm.value.postalCode;
        this.payMethodInfo.country = this.billForm.value.country;


        this.messageColor = "white";
        this.billMessage = "Please wait updating....";
        this.fintechService.setBusy(true);
        let stripeIntentId;

        const obs$ = this.fintechService.getPayerToken().pipe(
            takeUntil(this.destroy$),
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/stripe/createStripeIntent";
                const options = {token: payerToken};
                // Apply retry logic to the first POST call
                return this.http.post(url, options).pipe(
                    retry({
                        count: 5,
                        delay: (error, retryCount) => {
                            this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, {timeOut: 3000});
                            return of(error);
                        },
                    }),
                    catchError(error => {
                        console.error('Error creating Stripe intent after retries:', error);
                        this.toastr.error('Failed to connect to the server. Please try again later.', 'FinTech', {timeOut: 5000});
                        this.fintechService.setBusy(false);
                        return EMPTY; // Stop the observable chain
                    })
                );
            }),
            switchMap(ret => {
                const data: any = ret;
                if (!data || !data.stripeIntentId) {
                    return of(null);
                }
                stripeIntentId = data.stripeIntentId;

                return new Observable<any>(sub => {
                    this.fintechService.stripe.confirmCardSetup(data.clientSecret, {
                        payment_method: {
                            card: this.card,
                            billing_details: {
                                name: this.payMethodInfo.cardholder,
                                address: {
                                    line1: this.payMethodInfo.address1,
                                    line2: this.payMethodInfo.address2,
                                    city: this.payMethodInfo.city,
                                    state: this.payMethodInfo.state,
                                    postal_code: this.payMethodInfo.postalCode,
                                    country: this.payMethodInfo.country
                                }
                            }
                        }
                    }).then(ret => {
                        sub.next(ret);
                        sub.complete();
                    }).catch((error) => {
                        // Handle network or other errors
                        console.error('Error confirming card setup:', error);
                        sub.error();
                    });
                });
            }),
            switchMap(ret => {
                if (ret && ret.error) {
                    this.messageColor = "#ffc0c0";
                    this.billMessage = ret.error.message;
                    this.fintechService.setBusy(false);
                    return of(null);
                } else if (ret) {
                    return this.updateStripeBilling(stripeIntentId);
                } else {
                    return EMPTY; // Stop the chain if card setup failed
                }
            })
        );

        obs$.subscribe();
    }


    updateStripeBilling(intentId): Observable<any> {
        return this.fintechService.getPayerToken().pipe(
            switchMap(payerToken => {
                const url = "https://pay.signage.me:8443/stripe/updateStripeBilling";
                const options = {token: payerToken, stripeIntentId: intentId};
                // Apply retry logic to the second POST call
                return this.http.post(url, options).pipe(
                    retry({
                        count: 5,
                        delay: (error, retryCount) => {
                            this.toastr.info(`Please wait connecting to server... (${retryCount})`, `FinTech`, {timeOut: 3000});
                            return of(error);
                        },
                    }),
                    catchError(error => {
                        console.error('Error updating Stripe billing after retries:', error);
                        this.toastr.error('Failed to update billing information on the server.', 'FinTech', {timeOut: 5000});
                        this.fintechService.setBusy(false);
                        return EMPTY; // Stop the observable chain
                    })
                );
            }),
            switchMap(ret => {
                this.fintechService.data.recurringPayMethodId = (<any>ret).recurringPayMethodId;
                this.fintechService.data.orderPayMethodId = (<any>ret).orderPayMethodId;


                const payMethodInfo: any = (<any>ret).payMethodInfo;
                this.payMethodInfo.payMethodId = payMethodInfo.payMethodId;
                this.payMethodInfo.cardholder = payMethodInfo.cardholder;
                this.payMethodInfo.last4 = payMethodInfo.last4;
                this.payMethodInfo.expiration = payMethodInfo.expiration;
                this.payMethodInfo.address1 = payMethodInfo.address1;
                this.payMethodInfo.address2 = payMethodInfo.address2;
                this.payMethodInfo.city = payMethodInfo.city;
                this.payMethodInfo.state = payMethodInfo.state;
                this.payMethodInfo.postalCode = payMethodInfo.postalCode;
                this.payMethodInfo.country = payMethodInfo.country;
                this.messageColor = "white";
                this.billMessage = "";
                this.dialogRef.close(this.payMethodInfo);
                this.fintechService.setBusy(false);
                return of('a');
            }),
            takeUntil(this.destroy$)
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
